/**
 * Documents tab — S3-backed file upload/download/delete.
 *
 * Two-step upload: client gets a presigned PUT URL from
 * account.generateUploadUrl, PUTs the file directly to S3, then confirms via
 * account.confirmUpload (which creates the AccountsDocument row).
 */

import React from 'react';
import {Download, FileText, Trash2, Upload} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {Card} from '@common-web/components/ui/card';
import {Label} from '@common-web/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {trpc} from '@common-web/lib/trpc';
import {useClientActionAllowed, usePortalActionAllowed} from '@common-web/hooks/use-permissions';
import {useIsInternal} from '@common-web/providers/portalProvider';
import {CODE_PERMISSIONS} from '@common/permissions';
import type {AccountsDocumentCategory} from '@api/generated/client';
import {IntakePackagePanel} from '@client/pages/account/components/IntakePackagePanel';

interface DocumentsTabProps {
  accountsId: string;
  /** Jump to the RFC tab — passed through to the Intake Package panel. */
  onNavigateToRfc?: () => void;
  /** Read-only virtual VBA account — documents can't be stored, so the upload
   *  controls and the intake-package checklist (pipeline paperwork) hide. */
  readOnly?: boolean;
}

const CATEGORY_LABEL: Record<AccountsDocumentCategory, string> = {
  RFC_ATTACHMENT: 'RFC attachment',
  MSA: 'MSA',
  PROPOSAL: 'Proposal',
  GENERAL: 'General',
  OTHER: 'Other',
  INCOME_WORKSHEET: 'Income worksheet',
  W9: 'W-9',
  ACH_BANK_LETTER: 'ACH / bank letter',
  ERISA_PLAN_DOC: 'ERISA / plan document',
  SECTION_125_DOC: '§125 plan document',
  CLEAR_SPRING_FORM: 'Clear Spring form',
  PAYROLL_DATA: 'Payroll Data',
  CENSUS_FILE: 'Census File',
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentsTab({
  accountsId,
  onNavigateToRfc,
  readOnly = false,
}: DocumentsTabProps): React.ReactElement {
  const utils = trpc.useUtils();
  const isInternal = useIsInternal();
  // Mirrors the API gate on account.generateUploadUrl/confirmUpload: internal
  // users must hold the key; hides the upload controls when they don't.
  const canUpload = usePortalActionAllowed(CODE_PERMISSIONS.accounts.uploadDocuments.key);
  // Mirrors account.deleteDocument: user-uploaded docs require the edit-basic
  // client action key (internal users pass); system-generated docs (null
  // uploader, e.g. the materialized signed MSA) are internal-only.
  const canDeleteUserDocs = useClientActionAllowed(CODE_PERMISSIONS.accounts.editBasic.key);
  const [category, setCategory] = React.useState<AccountsDocumentCategory>('GENERAL');
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const docs = trpc.account.getDocuments.useQuery({accountsId});
  const account = trpc.account.getById.useQuery({accountsId});
  const clearSpringEnabled = account.data?.clearSpringFormEnabled ?? false;
  const listRef = React.useRef<HTMLDivElement>(null);
  const generateUploadUrl = trpc.account.generateUploadUrl.useMutation();
  const confirmUpload = trpc.account.confirmUpload.useMutation({
    onSuccess: () => {
      utils.account.getDocuments.invalidate({accountsId});
      utils.account.getActivity.invalidate({accountsId});
    },
  });
  const del = trpc.account.deleteDocument.useMutation({
    onSuccess: () => {
      utils.account.getDocuments.invalidate({accountsId});
      utils.account.getActivity.invalidate({accountsId});
    },
  });
  const downloadUrlMutation = trpc.account.generateDownloadUrl.useMutation();

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError(null);
    setUploading(true);
    try {
      const presigned = await generateUploadUrl.mutateAsync({
        accountsId,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        category,
      });

      const putRes = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {'Content-Type': file.type || 'application/octet-stream'},
      });
      if (!putRes.ok) {
        throw new Error(`Upload failed: ${putRes.status}`);
      }

      await confirmUpload.mutateAsync({
        accountsId,
        s3Key: presigned.s3Key,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        category,
      });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }

  async function handleDownload(documentId: string): Promise<void> {
    const {downloadUrl} = await downloadUrlMutation.mutateAsync({documentId});
    window.open(downloadUrl, '_blank');
  }

  /** Missing-essential shortcut: preset the category and open the picker. */
  function handleUploadMissing(missingCategory: string): void {
    setCategory(missingCategory as AccountsDocumentCategory);
    fileInputRef.current?.click();
  }

  return (
    <div className='space-y-4'>
      {readOnly ? null : (
        <IntakePackagePanel
          accountsId={accountsId}
          onNavigateToRfc={onNavigateToRfc}
          onViewDocuments={() =>
            listRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'})
          }
          onUploadMissing={canUpload ? handleUploadMissing : undefined}
        />
      )}
      <Card className='space-y-4 border-border p-6 shadow-sm'>
        <h3 className='text-sm font-semibold uppercase tracking-wide text-foreground'>Documents</h3>
        {readOnly || !canUpload ? null : (
          <div className='space-y-3 rounded-lg border border-border bg-muted/30 p-4'>
            <div className='grid grid-cols-[1fr_auto] gap-3 items-end'>
              <div className='space-y-1.5'>
                <Label htmlFor='doc-category'>Category</Label>
                <Select
                  value={category}
                  onValueChange={(v) => setCategory(v as AccountsDocumentCategory)}
                >
                  <SelectTrigger id='doc-category'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(CATEGORY_LABEL) as AccountsDocumentCategory[])
                      .filter((cat) => cat !== 'CLEAR_SPRING_FORM' || clearSpringEnabled)
                      .map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {CATEGORY_LABEL[cat]}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <Button disabled={uploading} onClick={() => fileInputRef.current?.click()}>
                <Upload className='w-4 h-4 mr-1' />
                {uploading ? 'Uploading…' : 'Upload Document'}
              </Button>
            </div>
            <input ref={fileInputRef} type='file' className='hidden' onChange={handleFileChange} />
            {uploadError ? <div className='text-sm text-red-600'>{uploadError}</div> : null}
          </div>
        )}

        {/* cbs-portal document table: indicator · Document · Type · Actions. */}
        <div ref={listRef}>
          {docs.isLoading ? (
            <div className='text-sm text-muted-foreground'>Loading documents…</div>
          ) : docs.data && docs.data.length > 0 ? (
            <div className='overflow-hidden rounded-lg border border-border'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b bg-muted/40 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                    <th className='w-10 px-3 py-2' aria-label='Status' />
                    <th className='px-3 py-2'>Document</th>
                    <th className='px-3 py-2'>Type</th>
                    <th className='w-24 px-3 py-2 text-right'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.data.map((d) => (
                    <tr key={d.id} className='border-b last:border-b-0 hover:bg-muted/30'>
                      <td className='px-3 py-2.5'>
                        <span className='flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary'>
                          <FileText className='h-3.5 w-3.5' />
                        </span>
                      </td>
                      <td className='px-3 py-2.5'>
                        <div className='min-w-0'>
                          <div className='truncate font-medium text-foreground'>{d.fileName}</div>
                          <div className='text-xs text-muted-foreground'>
                            {formatBytes(d.sizeBytes)} · {d.uploadedByUser?.name ?? 'System'} ·{' '}
                            {new Date(d.createdAt).toLocaleString()}
                          </div>
                        </div>
                      </td>
                      <td className='px-3 py-2.5'>
                        <span className='inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground'>
                          {CATEGORY_LABEL[d.category]}
                        </span>
                      </td>
                      <td className='px-3 py-2.5'>
                        <div className='flex items-center justify-end gap-1'>
                          <Button size='sm' variant='ghost' onClick={() => handleDownload(d.id)}>
                            <Download className='w-3 h-3' />
                          </Button>
                          {(d.uploadedByUser != null ? canDeleteUserDocs : isInternal) && (
                            <Button
                              size='sm'
                              variant='ghost'
                              onClick={() => del.mutate({documentId: d.id})}
                            >
                              <Trash2 className='w-3 h-3' />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='text-sm text-muted-foreground text-center py-6'>No documents yet.</div>
          )}
        </div>
      </Card>
    </div>
  );
}
